import React from 'react';

import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import ItemUnavailableMessage from 'components/item-unavailable-message';
import useDialogModal from 'hooks/use-dialog-modal';
import useMediaQuery from 'hooks/use-media-query';
import { IValidDayPart } from 'state/day-part/hooks/use-active-day-parts';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { OptionQuantityError } from 'utils/wizard/requirements';

import defaultConfig from './config';
import { ItemStatus } from './hooks/enums';

interface CtaButtonProps {
  // @ts-ignore
  buttonCSS: JSX.IntrinsicAttributes['css'];
  cta: {
    children: JSX.Element[];
    onClick: () => void;
  };
  currentPrice: string;
  dayParts: readonly IValidDayPart[];
  handleFindRestaurantClick: () => void;
  isCurrentPriceValid: boolean;
  isEditing: boolean;
  isFinalStep: boolean;
  itemDayParts: string[];
  itemStatus: ItemStatus;
  quantity: number;
  requirementsErrors: OptionQuantityError[];
  showActionOrMessageInBottom: boolean;
  titleText: string;
}

export const CtaButton: React.FC<CtaButtonProps> = ({
  buttonCSS,
  cta,
  currentPrice,
  dayParts,
  handleFindRestaurantClick,
  isCurrentPriceValid,
  isEditing,
  isFinalStep,
  itemDayParts,
  itemStatus,
  quantity,
  requirementsErrors,
  showActionOrMessageInBottom,
  titleText,
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery('mobile');

  const [ErrorModal, openErrorModal] = useDialogModal({
    modalAppearanceEventMessage: 'Menu option selection errors.',
  });

  const mParticle = useMParticleContext();

  switch (itemStatus) {
    case ItemStatus.CHECK_STORE_AVAILABILITY:
      return (
        <ActionButton
          variant={defaultConfig.buttonVariant}
          aria-label={formatMessage({ id: 'checkAvailability' })}
          data-testid="menu-select-restaurant-button"
          onClick={handleFindRestaurantClick}
          fullWidth
          onlyIcon={isMobile}
          css={buttonCSS}
        >
          {formatMessage({ id: 'checkAvailability' })}
        </ActionButton>
      );
    case ItemStatus.DAYPART_UNAVAILABLE:
      return (
        <ItemUnavailableMessage
          dayParts={dayParts}
          itemDayParts={itemDayParts}
          marginAndBorders={!showActionOrMessageInBottom}
        />
      );
    case ItemStatus.AVAILABLE_IN_STORE:
      const UPDATE_STRING = 'updateQuantityAndItemWithPrice';
      const ADD_STRING = 'addQuantityAndItemWithPrice';
      const messageId = !isFinalStep ? 'continue' : isEditing ? UPDATE_STRING : ADD_STRING;
      const ariaLabel = formatMessage(
        { id: messageId },
        { itemName: titleText, quantity, price: currentPrice }
      );
      // only log continue clicks because add to order is logged within OrderContext
      const eventName = isFinalStep
        ? undefined
        : CustomEventNames.BUTTON_CLICK_MENU_WIZARD_CONTINUE;

      const ctaContinuation =
        isFinalStep && requirementsErrors.length ? openErrorModal : cta.onClick;

      const handleClick = () => {
        if (messageId === UPDATE_STRING || messageId === ADD_STRING) {
          mParticle.logRBIEvent({
            name: CustomEventNames.CLICK_EVENT,
            type: EventTypes.Navigation,
            attributes: {
              component: ClickEventComponentNames.BUTTON,
              text: messageId === UPDATE_STRING ? 'Product Update' : 'Product Addition',
            },
          });
        }
        ctaContinuation();
      };
      return (
        <>
          <ActionButton
            onlyIcon={isMobile}
            variant={defaultConfig.buttonVariant}
            aria-label={ariaLabel}
            data-testid="menu-action-button"
            perceptible
            disabled={!isCurrentPriceValid}
            onClick={handleClick}
            eventName={eventName}
            skipLogEvent={isFinalStep}
            fullWidth
            css={buttonCSS}
          >
            {cta.children.join(' — ')}
          </ActionButton>

          <ErrorModal
            heading={formatMessage({ id: 'missingItems' })}
            body={requirementsErrors.map(error => (
              <p key={error.name}>
                {formatMessage(
                  { id: 'makeAdditionalSelections' },
                  { name: error.name, quantity: error.minAmount - error.quantity }
                )}
              </p>
            ))}
          />
        </>
      );
    case ItemStatus.MAX_CART_QUANTITY_MET: {
      const errorMessage = formatMessage({ id: 'limitReached' });
      return (
        <ActionButton
          variant={defaultConfig.buttonVariant}
          aria-label={errorMessage}
          data-testid="menu-action-button"
          perceptible
          disabled
          onClick={cta.onClick}
          skipLogEvent
          fullWidth
          css={buttonCSS}
        >
          {errorMessage}
        </ActionButton>
      );
    }
    default:
      return null;
  }
};
